import { DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '../../../global/config/api';
import { getFilters } from '../../../global/helpers/filters';
import { useServerCall } from '../../../global/helpers/hooks';
import CodelistStore from '../../../global/stores/CodelistStore';
import { IAttachment } from '../../../global/types/types';
import { XsBaseDialog, XsButton, XsDropzone, XsNumberField, XsSearchSelect, XsTextField } from '../../../global/ui';
import {
  ICodelistRow,
  IEnergeticDeviceAttachedFile,
  IEnergeticDeviceSave,
  IEnergeticDeviceSaveResponse
} from '../../../swagger/gd/codelist';
import DeviceDialogStore from './DeviceDialogStore';

// ─── Dynamická konfigurácia polí/príloh podľa Druhu zariadenia (energetic_device_type_id) ────
// Zodpovedá 8 tvarom tela pre PUT /gd/record/energetic-device — API volanie zatiaľ nie je
// napojené (len UI shell), pribudne neskôr. Poradie polí a texty príloh vychádzajú z dizajnu
// (screenshoty pre PV/BB/VC/SC/HP/WT) — pre BA/TS bez dizajnu ostáva pôvodný odhad.
type TNumericFieldKey = 'power' | 'capacity' | 'cooling_power' | 'high_temp_power' | 'gwp';

type TFieldDescriptor =
  | { kind: 'numeric'; key: TNumericFieldKey; labelKey: string; required?: boolean }
  | { kind: 'energyEfficiencyClass'; labelKey: string }
  | { kind: 'refrigerant'; labelKey: string };

interface IAttachmentSlotConfig {
  type: string;
  labelKey: string;
  maxFiles: number;
}

interface IDeviceTypeFieldConfig {
  fields: TFieldDescriptor[];
  attachments: IAttachmentSlotConfig[];
}

const DOC_ACCEPT = ['.pdf', '.jpg', '.jpeg', '.png'];

const DEVICE_TYPE_FIELD_CONFIG: Record<string, IDeviceTypeFieldConfig> = {
  // Akumulátor — bez dizajnu, ponechaný pôvodný odhad
  BA: {
    fields: [{ kind: 'numeric', key: 'capacity', labelKey: 'device_dialog.new_device_capacity', required: true }],
    attachments: [
      { type: 'GD:ENERDEVICE:BA:TECHNICALSHEET', labelKey: 'device_dialog.new_device_att_technical_sheet', maxFiles: 5 },
      { type: 'GD:ENERDEVICE:BA:PRODUCTIONLABEL', labelKey: 'device_dialog.new_device_att_production_label', maxFiles: 5 }
    ]
  },
  // Kotol na biomasu
  BB: {
    fields: [
      { kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true },
      { kind: 'energyEfficiencyClass', labelKey: 'device_dialog.new_device_energy_efficiency_class_bb' }
    ],
    attachments: [
      { type: 'GD:ENERDEVICE:BB:DECLARCONFORMITY', labelKey: 'device_dialog.new_device_att_declar_conformity', maxFiles: 3 },
      { type: 'GD:ENERDEVICE:BB:ENERGYLABEL', labelKey: 'device_dialog.new_device_att_energy_label', maxFiles: 2 }
    ]
  },
  // Tepelné čerpadlo
  HP: {
    fields: [
      { kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true },
      { kind: 'numeric', key: 'cooling_power', labelKey: 'device_dialog.new_device_cooling_power' },
      { kind: 'numeric', key: 'high_temp_power', labelKey: 'device_dialog.new_device_high_temp_power' },
      { kind: 'energyEfficiencyClass', labelKey: 'device_dialog.new_device_energy_efficiency_class' },
      { kind: 'refrigerant', labelKey: 'device_dialog.new_device_refrigerant' },
      { kind: 'numeric', key: 'gwp', labelKey: 'device_dialog.new_device_gwp' }
    ],
    attachments: [
      {
        type: 'GD:ENERDEVICE:HP:REFRIGERANTDOCUMENT',
        labelKey: 'device_dialog.new_device_att_refrigerant_document',
        maxFiles: 3
      },
      {
        type: 'GD:ENERDEVICE:HP:LIFETIMECONFIRMATION',
        labelKey: 'device_dialog.new_device_att_lifetime_confirmation_10',
        maxFiles: 2
      },
      { type: 'GD:ENERDEVICE:HP:ENERGYLABEL', labelKey: 'device_dialog.new_device_att_energy_label', maxFiles: 4 },
      {
        type: 'GD:ENERDEVICE:HP:DECLARCONFORMITY',
        labelKey: 'device_dialog.new_device_att_declar_conformity_hp',
        maxFiles: 4
      }
    ]
  },
  // Fotovoltický panel
  PV: {
    fields: [{ kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true }],
    attachments: [
      {
        type: 'GD:ENERDEVICE:PV:POWERTESTPROTOCOL',
        labelKey: 'device_dialog.new_device_att_power_test_protocol_pv',
        maxFiles: 3
      },
      { type: 'GD:ENERDEVICE:PV:DECLARCONFORMITY', labelKey: 'device_dialog.new_device_att_declar_conformity', maxFiles: 2 },
      {
        type: 'GD:ENERDEVICE:PV:LIFETIMECONFIRMATION',
        labelKey: 'device_dialog.new_device_att_lifetime_confirmation_20',
        maxFiles: 4
      }
    ]
  },
  // Slnečný kolektor
  SC: {
    fields: [{ kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true }],
    attachments: [
      {
        type: 'GD:ENERDEVICE:SC:POWERTESTPROTOCOL',
        labelKey: 'device_dialog.new_device_att_power_test_protocol_sc',
        maxFiles: 3
      },
      {
        type: 'GD:ENERDEVICE:SC:DECLARCONFORMITY',
        labelKey: 'device_dialog.new_device_att_declar_conformity_sc',
        maxFiles: 2
      },
      {
        type: 'GD:ENERDEVICE:SC:LIFETIMECONFIRMATION',
        labelKey: 'device_dialog.new_device_att_lifetime_confirmation_20',
        maxFiles: 4
      }
    ]
  },
  // Akumulačná nádrž — bez dizajnu, ponechaný pôvodný odhad
  TS: {
    fields: [{ kind: 'numeric', key: 'capacity', labelKey: 'device_dialog.new_device_capacity', required: true }],
    attachments: [
      { type: 'GD:ENERDEVICE:TS:PRODUCTIONLABEL', labelKey: 'device_dialog.new_device_att_production_label', maxFiles: 5 },
      { type: 'GD:ENERDEVICE:TS:TECHNICALSHEET', labelKey: 'device_dialog.new_device_att_technical_sheet', maxFiles: 5 }
    ]
  },
  // Menič napätia
  VC: {
    fields: [{ kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true }],
    attachments: [
      {
        type: 'GD:ENERDEVICE:VC:TECHNICALSHEET',
        labelKey: 'device_dialog.new_device_att_technical_sheet_vc',
        maxFiles: 3
      },
      {
        type: 'GD:ENERDEVICE:VC:LIFETIMECONFIRMATION',
        labelKey: 'device_dialog.new_device_att_lifetime_confirmation_10',
        maxFiles: 2
      }
    ]
  },
  // Veterná turbína
  WT: {
    fields: [{ kind: 'numeric', key: 'power', labelKey: 'device_dialog.new_device_power', required: true }],
    attachments: [
      {
        type: 'GD:ENERDEVICE:WT:POWERTESTPROTOCOL',
        labelKey: 'device_dialog.new_device_att_power_test_protocol_wt',
        maxFiles: 3
      },
      { type: 'GD:ENERDEVICE:WT:DECLARCONFORMITY', labelKey: 'device_dialog.new_device_att_declar_conformity', maxFiles: 2 },
      {
        type: 'GD:ENERDEVICE:WT:LIFETIMECONFIRMATION',
        labelKey: 'device_dialog.new_device_att_lifetime_confirmation_15',
        maxFiles: 4
      }
    ]
  }
};

interface IProps {
  open: boolean;
  onClose: () => void;
  // Zavolané až po úspešnom uložení zariadenia cez PUT /gd/record/energetic-device
  onConfirm: () => void;
  // Prevzaté z InstalledDeviceSelectDialog (tam už boli vybrané) — predvyplnia a zablokujú
  // polia Druh/Špecifikácia/Výrobca zariadenia
  presetTypeCode?: string | null;
  presetSpecificationId?: string | null;
  presetManufacturerCode?: string | null;
}

function AddNewDeviceDialog({
  open,
  onClose,
  onConfirm,
  presetTypeCode = null,
  presetSpecificationId = null,
  presetManufacturerCode = null
}: IProps) {
  const { t } = useTranslation();

  const [typeCode, setTypeCode] = useState<string | null>(null);
  const [specificationId, setSpecificationId] = useState<string | null>(null);
  const [specificationOptions, setSpecificationOptions] = useState<ICodelistRow[]>([]);
  const [manufacturerCode, setManufacturerCode] = useState<string | null>(null);
  const [modelName, setModelName] = useState('');
  const [numericValues, setNumericValues] = useState<Partial<Record<TNumericFieldKey, number | ''>>>({});
  const [energyEfficiencyClassId, setEnergyEfficiencyClassId] = useState<string | null>(null);
  const [refrigerantId, setRefrigerantId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Record<string, IAttachment[]>>({});

  const energeticDeviceTypeCall = useServerCall<unknown, { rows: ICodelistRow[] }>({
    key: 'energeticDeviceTypeCodelist',
    mode: 'control',
    request: { method: 'POST', url: API.energeticDeviceTypeCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => CodelistStore.setCodelist('energetic_device_type', res.data?.rows ?? [])
  });

  // Druh zariadenia sa filtruje podľa device_group_id kola (devide_group_id z /rounds — 'ele' | 'heat')
  useEffect(() => {
    if (DeviceDialogStore.deviceGroupId) {
      energeticDeviceTypeCall.run({ data: getFilters([`device_group_id=${DeviceDialogStore.deviceGroupId}`]) });
    }
  }, [DeviceDialogStore.deviceGroupId]);

  useServerCall<undefined, { rows: ICodelistRow[] }>({
    key: 'manufacturerCodelist',
    mode: 'auto',
    request: { method: 'GET', url: API.manufacturerCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => CodelistStore.setCodelist('manufacturer', res.data?.rows ?? [])
  });

  // Číselníky pre Tepelné čerpadlo (HP) — trieda energetickej účinnosti a chladivo
  useServerCall<undefined, { rows: ICodelistRow[] }>({
    key: 'energyEfficiencyClassCodelist',
    mode: 'auto',
    request: { method: 'GET', url: API.energyEfficiencyClassCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => CodelistStore.setCodelist('energy_efficiency_class', res.data?.rows ?? [])
  });

  useServerCall<undefined, { rows: ICodelistRow[] }>({
    key: 'refrigerantCodelist',
    mode: 'auto',
    request: { method: 'GET', url: API.refrigerantCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => CodelistStore.setCodelist('refrigerant', res.data?.rows ?? [])
  });

  const typeOptions = CodelistStore.getCodelist<ICodelistRow>('energetic_device_type');
  const selectedType = typeOptions.find((o) => o.code_ext === typeCode) ?? null;
  const manufacturerOptions = CodelistStore.getCodelist<ICodelistRow>('manufacturer');
  const selectedManufacturer = manufacturerOptions.find((o) => o.code_ext === manufacturerCode) ?? null;
  const energyEfficiencyClassOptions = CodelistStore.getCodelist<ICodelistRow>('energy_efficiency_class');
  const selectedEnergyEfficiencyClass =
    energyEfficiencyClassOptions.find((o) => o.code_ext === energyEfficiencyClassId) ?? null;
  const refrigerantOptions = CodelistStore.getCodelist<ICodelistRow>('refrigerant');
  const selectedRefrigerant = refrigerantOptions.find((o) => o.code_ext === refrigerantId) ?? null;

  const specificationCall = useServerCall<unknown, { rows: ICodelistRow[] }>({
    key: 'energeticDeviceSpecificationByTypeForNewDevice',
    mode: 'control',
    request: { method: 'POST', url: API.energeticDeviceSpecificationCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => setSpecificationOptions(res.data?.rows ?? [])
  });

  // Špecifikácie závisia od zvoleného druhu zariadenia — dotiahnuť nanovo pri každej zmene typu
  useEffect(() => {
    if (selectedType?._id) {
      specificationCall.run({ data: getFilters([`energetic_device_type_id=${selectedType._id}`]) });
    } else {
      setSpecificationOptions([]);
    }
  }, [selectedType?._id]);

  useEffect(() => {
    if (open) {
      setTypeCode(presetTypeCode);
      setSpecificationId(presetSpecificationId);
      setManufacturerCode(presetManufacturerCode);
      setModelName('');
      setNumericValues({});
      setEnergyEfficiencyClassId(null);
      setRefrigerantId(null);
      setAttachments({});
    }
  }, [open, presetTypeCode, presetSpecificationId, presetManufacturerCode]);

  const selectedSpecification = specificationOptions.find((o) => o._id === specificationId) ?? null;
  const fieldConfig = typeCode ? DEVICE_TYPE_FIELD_CONFIG[typeCode] : null;

  // Zmena Druhu zariadenia mení sadu dynamických polí/príloh — predchádzajúce hodnoty už nesedia
  useEffect(() => {
    setNumericValues({});
    setEnergyEfficiencyClassId(null);
    setRefrigerantId(null);
    setAttachments({});
  }, [typeCode]);

  // Povinné je len hlavné technické pole (Výkon/Kapacita) — ostatné dynamické polia sú voliteľné
  const requiredNumericKeys = (fieldConfig?.fields ?? [])
    .filter((f): f is Extract<TFieldDescriptor, { kind: 'numeric' }> => f.kind === 'numeric' && !!f.required)
    .map((f) => f.key);

  const isComplete =
    !!selectedType &&
    !!selectedSpecification &&
    !!selectedManufacturer &&
    modelName.trim() !== '' &&
    !!fieldConfig &&
    requiredNumericKeys.every((k) => numericValues[k] !== undefined && numericValues[k] !== '');

  const saveDeviceCall = useServerCall<IEnergeticDeviceSave, IEnergeticDeviceSaveResponse>({
    key: 'saveEnergeticDevice',
    mode: 'control',
    request: { method: 'PUT', url: API.saveEnergeticDevice() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: () => onConfirm()
  });

  const handleConfirm = () => {
    if (!selectedType || !selectedSpecification || !selectedManufacturer || !fieldConfig) return;
    const hasEnergyEfficiencyClass = fieldConfig.fields.some((f) => f.kind === 'energyEfficiencyClass');
    const hasRefrigerant = fieldConfig.fields.some((f) => f.kind === 'refrigerant');

    const attachedFiles: IEnergeticDeviceAttachedFile[] = fieldConfig.attachments.flatMap((slot) =>
      (attachments[slot.type] ?? []).map((att) => ({
        type: slot.type,
        content_type: att.content_type,
        file_name: att.file_name,
        content: att.content
      }))
    );

    const payload: IEnergeticDeviceSave = {
      energetic_device_type_id: selectedType._id,
      energetic_device_specification_id: selectedSpecification._id,
      manufacturer_id: selectedManufacturer._id,
      model_name: modelName.trim(),
      energy_efficiency_class_id: hasEnergyEfficiencyClass ? (energyEfficiencyClassId ?? undefined) : undefined,
      refrigerant_id: hasRefrigerant ? (refrigerantId ?? undefined) : undefined,
      attached_files: attachedFiles.length > 0 ? attachedFiles : undefined
    };
    fieldConfig.fields.forEach((f) => {
      if (f.kind !== 'numeric') return;
      const v = numericValues[f.key];
      if (v !== undefined && v !== '') payload[f.key] = Number(v);
    });

    saveDeviceCall.run({ data: payload });
  };

  return (
    <XsBaseDialog
      open={open}
      onClose={onClose}
      title={t('device_dialog.new_device_title')}
      maxWidth='sm'
      fullWidth
      sx={{ '& .MuiDialog-paper': { maxWidth: 780 } }}
    >
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='newDeviceType'
              label={t('device_dialog.installed_type')}
              required
              disabled={!!presetTypeCode}
              value={selectedType}
              options={typeOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o.code_ext ?? o._id}
              onChange={(v: ICodelistRow | null) => {
                setTypeCode(v?.code_ext ?? null);
                setSpecificationId(null);
              }}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='newDeviceSpecification'
              label={t('device_dialog.installed_specification')}
              required
              disabled={!!presetSpecificationId || !typeCode}
              value={selectedSpecification}
              options={specificationOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o._id}
              onChange={(v: ICodelistRow | null) => setSpecificationId(v?._id ?? null)}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            <XsSearchSelect<ICodelistRow>
              name='newDeviceManufacturer'
              label={t('device_dialog.installed_manufacturer')}
              required
              disabled={!!presetManufacturerCode}
              value={selectedManufacturer}
              options={manufacturerOptions}
              getOptionLabel={(o) => o.name_ext ?? ''}
              getOptionValue={(o) => o.code_ext ?? o._id}
              onChange={(v: ICodelistRow | null) => setManufacturerCode(v?.code_ext ?? null)}
              onBlur={() => {}}
            />
          </Grid>

          <Grid item xs={12}>
            <XsTextField
              name='newDeviceModel'
              label={t('device_dialog.installed_model')}
              required
              value={modelName}
              onChange={(e: any) => setModelName(e.target.value)}
            />
          </Grid>

          {fieldConfig?.fields.map((field) => {
            if (field.kind === 'numeric') {
              return (
                <Grid item xs={12} key={field.key}>
                  <XsNumberField
                    name={`newDevice_${field.key}`}
                    label={t(field.labelKey)}
                    required={field.required}
                    value={numericValues[field.key] ?? ''}
                    onChange={(e: any) =>
                      setNumericValues((prev) => ({
                        ...prev,
                        [field.key]: e.target.value === '' ? '' : Number(e.target.value)
                      }))
                    }
                    inputProps={{ min: 0, step: 0.001 }}
                  />
                </Grid>
              );
            }
            if (field.kind === 'energyEfficiencyClass') {
              return (
                <Grid item xs={12} key='energyEfficiencyClass'>
                  <XsSearchSelect<ICodelistRow>
                    name='newDeviceEnergyEfficiencyClass'
                    label={t(field.labelKey)}
                    value={selectedEnergyEfficiencyClass}
                    options={energyEfficiencyClassOptions}
                    getOptionLabel={(o) => o.name_ext ?? ''}
                    getOptionValue={(o) => o.code_ext ?? o._id}
                    onChange={(v: ICodelistRow | null) => setEnergyEfficiencyClassId(v?.code_ext ?? null)}
                    onBlur={() => {}}
                  />
                </Grid>
              );
            }
            return (
              <Grid item xs={12} key='refrigerant'>
                <XsSearchSelect<ICodelistRow>
                  name='newDeviceRefrigerant'
                  label={t(field.labelKey)}
                  value={selectedRefrigerant}
                  options={refrigerantOptions}
                  getOptionLabel={(o) => o.name_ext ?? ''}
                  getOptionValue={(o) => o.code_ext ?? o._id}
                  onChange={(v: ICodelistRow | null) => setRefrigerantId(v?.code_ext ?? null)}
                  onBlur={() => {}}
                />
              </Grid>
            );
          })}

          {fieldConfig && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
                {t('device_dialog.new_device_documentation_title')}
              </Typography>
              <Grid container spacing={2}>
                {fieldConfig.attachments.map((slot) => (
                  <Grid item xs={12} md={6} key={slot.type}>
                    <Typography variant='body2' sx={{ mb: 1 }}>
                      {t(slot.labelKey)}
                    </Typography>
                    <XsDropzone
                      name={`dropzone_newDevice_${slot.type}`}
                      maxFiles={slot.maxFiles}
                      accept={DOC_ACCEPT}
                      value={attachments[slot.type] ?? []}
                      onChange={(files) => setAttachments((prev) => ({ ...prev, [slot.type]: files as IAttachment[] }))}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <XsButton fullWidth={false} label='global.back' variant='outlined' onClick={onClose} />
          </Grid>
          <Grid item>
            <XsButton
              fullWidth={false}
              label='device_dialog.select_save_continue'
              variant='contained'
              color='success'
              disabled={!isComplete || saveDeviceCall.isPending}
              onClick={handleConfirm}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </XsBaseDialog>
  );
}

export default observer(AddNewDeviceDialog);
